import { useQuery } from '@tanstack/react-query';
import {
  gqlClient,
  graphql,
  type QueryAdditionalOptions,
  queryClient,
  type UseQueryReturnType,
} from '@tyro/api';
import { peopleKeys } from '../../keys';

const studentMedicalData = graphql(/* GraphQL */ `
  query wellbeing_studentMedical($filter: StudentMedicalFilter) {
    wellbeing_studentMedical(filter: $filter) {
      studentPartyId
      student {
        contacts {
          partyId
          person {
            firstName
            lastName
            type
            title {
              id
              name
              nameTextId
            }
          }
          personalInformation {
            preferredFirstName
            preferredLastName
            primaryPhoneNumber {
              number
            }
          }
          relationships {
            relationshipType
          }
        }
        siblings {
          enrolledSiblings {
            partyId
            person {
              title {
                id
                nameTextId
                name
              }
              firstName
              lastName
              avatarUrl
              type
            }
            classGroup {
              name
            }
          }
          nonEnrolledSiblings {
            partyId
            firstName
            lastName
          }
        }
      }
      conditions {
        id
        name
        description
        equipment {
          id
          name
          expiryDate
          location
          notes
        }
        emergencyPlan
      }
      medicalContacts {
        id
        personalTitle
        firstName
        lastName
        occupation
        addressLine1
        addressLine2
        addressLine3
        county
        postcode
        primaryPhone
        email
      }
    }
  }
`);

const studentMedicalConditions = graphql(/* GraphQL */ `
  query wellbeing_studentMedicalConditions($filter: StudentMedicalFilter) {
    wellbeing_studentMedical(filter: $filter) {
      conditions {
        id
        name
        description
        equipment {
          id
          name
          expiryDate
          location
          notes
        }
        emergencyPlan
      }
    }
  }
`);

const studentMedicalDataQuery = (studentId: number) => ({
  queryKey: peopleKeys.students.medical(studentId),
  queryFn: async () =>
    gqlClient.request(studentMedicalData, {
      filter: { studentPartyId: studentId ?? 0 },
    }),
});

export function getStudentMedicalData(studentId: number) {
  return queryClient.fetchQuery(studentMedicalDataQuery(studentId));
}

export function useStudentMedicalData(
  studentId: number,
  additionalOptions?: Omit<QueryAdditionalOptions, 'academicNamespaceId'>,
) {
  return useQuery({
    ...studentMedicalDataQuery(studentId),
    ...additionalOptions,
    select: ({ wellbeing_studentMedical }) => wellbeing_studentMedical,
  });
}

const studentMedicalConditionsQuery = (studentId: number) => ({
  queryKey: peopleKeys.students.studentMedicalConditions(studentId),
  queryFn: async () =>
    gqlClient.request(studentMedicalConditions, {
      filter: { studentPartyId: studentId ?? 0 },
    }),
});

export function getStudentMedicalConditions(studentId: number) {
  return queryClient.fetchQuery(studentMedicalConditionsQuery(studentId));
}

export function useStudentMedicalConditions(
  studentId: number,
  additionalOptions?: Omit<QueryAdditionalOptions, 'academicNamespaceId'>,
) {
  return useQuery({
    ...studentMedicalConditionsQuery(studentId),
    ...additionalOptions,
    select: ({ wellbeing_studentMedical }) =>
      wellbeing_studentMedical?.conditions ?? [],
  });
}

export type ReturnTypeFromUseStudentMedical = UseQueryReturnType<
  typeof useStudentMedicalData
>;

export type ReturnTypeFromUseStudentMedicalConditions = UseQueryReturnType<
  typeof useStudentMedicalConditions
>;
